/**
 * Static role → permission mapping, shared by the backend guard and the frontend UI gate.
 * Roles are additive sets of elevated permissions; baseline access needs no role.
 */

import { ALL_ACCOUNT_PERMISSIONS, Permission } from './permission';
import { MembershipSource } from './membership-source';
import { UserRole } from './user-role';

/**
 * Mapping of each role to its elevated permissions.
 * Users can have multiple roles — permissions are aggregated (union).
 */
export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  ADMIN: ALL_ACCOUNT_PERMISSIONS,

  SECURITY_ADMIN: [
    Permission.USERS_MANAGE,
    Permission.ACCOUNT_SETTINGS,
    Permission.API_KEYS_MANAGE,
    Permission.WEBHOOKS_MANAGE,
    Permission.INTEGRATIONS_MANAGE
  ],

  ACCOUNTANT: [
    Permission.INVOICES_EDIT,
    Permission.INVOICES_APPROVE,
    Permission.INVOICES_EXPORT,
    Permission.INVOICES_MATCH,
    Permission.PARTIES_MANAGE,
    Permission.BANK_ACCOUNTS_VERIFY,
    Permission.PAYMENTS_RECONCILE,
    Permission.PAYMENTS_PREPARE,
    Permission.PAYMENTS_RECORD,
    Permission.BANK_STATEMENTS_IMPORT,
    Permission.DOCUMENTS_REPROCESS,
    Permission.DOCUMENTS_DELETE,
    Permission.RULES_VIEW
  ],

  RULES_MANAGER: [Permission.RULES_MANAGE, Permission.RULES_VIEW],

  CODE_LISTS_MANAGER: [
    Permission.COST_CENTERS_MANAGE,
    Permission.JOBS_MANAGE,
    Permission.ORG_UNITS_MANAGE,
    Permission.PRODUCTS_MANAGE,
    Permission.ACCOUNTING_CODES_MANAGE
  ],

  APPROVER: [
    Permission.INVOICES_APPROVE,
    Permission.REQUISITIONS_APPROVE,
    Permission.PURCHASE_ORDERS_APPROVE,
    Permission.PAYMENTS_APPROVE,
    Permission.PAYMENTS_RELEASE,
    // Decides statement proposals of runs it did not build (ADR payment ledger D11, D12).
    Permission.PAYMENTS_RECONCILE
  ],

  SYSTEM_BILLING_ADMIN: [Permission.SYSTEM_BILLING],
  // Platform operations, from AMS: impersonation, the bridge plugin catalogue and tenant data.
  SYSTEM_SUPPORT_ADMIN: [
    Permission.SYSTEM_IMPERSONATE,
    Permission.SYSTEM_PLUGIN_RELEASES,
    Permission.SYSTEM_TENANT_DATA
  ]
};

/** Whether a set of roles includes a specific permission (union across roles). */
export function rolesHavePermission(roles: readonly UserRole[], permission: Permission): boolean {
  return roles.some(role => {
    const permissions = ROLE_PERMISSIONS[role];
    return permissions?.includes(permission) ?? false;
  });
}

/** All permissions granted by a set of roles (union). */
export function getAggregatedPermissions(roles: readonly UserRole[]): readonly Permission[] {
  const permissionSet = new Set<Permission>();
  for (const role of roles) {
    const permissions = ROLE_PERMISSIONS[role];
    if (permissions) {
      for (const p of permissions) {
        permissionSet.add(p);
      }
    }
  }
  return [...permissionSet];
}

/** Held only through a DIRECT membership: an accounting office prepares, the client approves. */
export const DIRECT_MEMBERSHIP_PERMISSIONS: readonly Permission[] = [
  Permission.PAYMENTS_APPROVE,
  Permission.PAYMENTS_RELEASE
];

/** Whether a membership's roles grant a permission; a missing source fails closed. */
export function membershipHasPermission(
  roles: readonly UserRole[],
  source: MembershipSource | null | undefined,
  permission: Permission
): boolean {
  if (source !== MembershipSource.DIRECT && DIRECT_MEMBERSHIP_PERMISSIONS.includes(permission)) {
    return false;
  }
  return rolesHavePermission(roles, permission);
}
